import type { AuditEvent } from '@magimp/shared';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../i18n/index.js';
import { Drawer, DetailSheet } from '../../../ui/index.js';
import { AUDIT_EVENT_LABEL_KEYS } from './AuditTable.js';

export interface AuditDetailSheetProps {
  event: AuditEvent;
  onClose: () => void;
}

export function AuditDetailSheet({ event, onClose }: AuditDetailSheetProps) {
  const { t } = useTranslation('audit');
  const actionLabel = t(AUDIT_EVENT_LABEL_KEYS[event.action], {
    defaultValue: event.action,
  });
  const actorLabel = event.actor ? `${event.actor.name} (${event.actor.email})` : t('actorDeleted');
  return (
    <Drawer onClose={onClose}>
      <DetailSheet title={t('detailTitle', { action: actionLabel })} onClose={onClose}>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Row label={t('fields.time')} value={formatDateTime(event.at)} />
          <Row label={t('fields.actor')} value={actorLabel} />
          <Row label={t('fields.action')} value={actionLabel} />
          <Row
            label={t('fields.target')}
            value={`${event.targetType}${event.targetId ? `:${event.targetId}` : ''}`}
          />
          <Row label="reqId" value={event.reqId ?? '—'} />
          <Row label={t('fields.reason')} value={event.reason ?? '—'} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Diff</div>
            {event.diff === null ? (
              <div style={{ color: 'var(--fg-2)' }}>{t('noDiff')}</div>
            ) : (
              <pre
                style={{
                  background: 'var(--bg-1)',
                  padding: 12,
                  borderRadius: 'var(--r-1)',
                  overflow: 'auto',
                  margin: 0,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                }}
              >
                {JSON.stringify(event.diff, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </DetailSheet>
    </Drawer>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ width: 120, fontWeight: 600, color: 'var(--fg-2)' }}>{label}</div>
      <div style={{ flex: 1, wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}
